import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";

function ErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: (...args: Array<unknown>) => void;
}) {
  useEffect(() => {
    // Log error when it occurs
    console.error("[Error Boundary]", {
      name: error.name,
      message: error.message,
      stack: error.stack,
    });
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Something unexpected happened
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            We've logged the error and will look into it.
          </p>
          {process.env.NODE_ENV === "development" && (
            <pre className="mt-4 text-left text-xs bg-gray-100 p-4 rounded overflow-auto">
              {error.message}
            </pre>
          )}
        </div>
        <div className="mt-5 flex justify-center">
          <button
            onClick={resetErrorBoundary}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
}

type AppErrorProps = {
  children: React.ReactNode;
};

export function AppErrorBoundary({ children }: AppErrorProps) {
  const navigate = useNavigate();

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // Navigate to a safe route when resetting
        navigate("/", { replace: true });
      }}
      onError={(error: Error) => {
        // Log error when boundary catches it
        console.error("[Error Boundary Caught]", {
          name: error.name,
          message: error.message,
          stack: error.stack,
        });
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
